import { Button, Modal, TextField } from '@nike/eds'
import { useState } from 'react'

import {
  exceedsMaxRlpnExportRange,
  MAX_RLPN_EXPORT_RANGE_ERROR
} from './rlpnExportDateRange'
import './ExportRlpnLinksModal.scss'

interface ExportRlpnLinksModalProps {
  showModal: boolean
  onDismiss: () => void
  onExport: (params: Record<string, string>) => Promise<boolean>
  exporting: boolean
}

export const ExportRlpnLinksModal = ({
  showModal,
  onDismiss,
  onExport,
  exporting
}: ExportRlpnLinksModalProps) => {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [startDateError, setStartDateError] = useState('')
  const [endDateError, setEndDateError] = useState('')

  const resetFields = () => {
    setStartDate('')
    setEndDate('')
    setStartDateError('')
    setEndDateError('')
  }

  const handleDismiss = () => {
    resetFields()
    onDismiss()
  }

  const handleExport = async () => {
    if (!startDate.trim()) {
      setStartDateError('Start date is required')
      return
    }
    if (endDate.trim() && endDate.trim() < startDate.trim()) {
      setEndDateError('End date must be on or after start date')
      return
    }
    if (exceedsMaxRlpnExportRange(startDate, endDate)) {
      setEndDateError(MAX_RLPN_EXPORT_RANGE_ERROR)
      return
    }

    const params: Record<string, string> = { linkedFrom: startDate.trim() }
    if (endDate.trim()) params.linkedTo = endDate.trim()
    const succeeded = await onExport(params)
    if (succeeded) {
      resetFields()
    }
  }

  const endBeforeStart = startDate.trim().length > 0
    && endDate.trim().length > 0
    && endDate.trim() < startDate.trim()

  const rangeExceeded = startDate.trim().length > 0
    && !endBeforeStart
    && exceedsMaxRlpnExportRange(startDate, endDate)

  const rangeWithinLimit = startDate.trim().length > 0 && !rangeExceeded

  const canExport = rangeWithinLimit
    && !endBeforeStart
    && !exporting

  return (
    <Modal
      isOpen={showModal}
      onDismiss={handleDismiss}
      headerSlot={
        <div className="flex justify-between items-center">
          <h3>Export rLPN List</h3>
        </div>
      }
      footerSlot={
        <div className="flex justify-end space-x-4">
          <Button onClick={handleDismiss} variant="secondary" disabled={exporting}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={!canExport}>
            {exporting ? 'Exporting...' : 'Download CSV'}
          </Button>
        </div>
      }
    >
      <div className="rlpn-export-modal-content">
        <p className="mb-4">Select date range (maximum 90 days)</p>
        <div className="rlpn-export-modal-fields">
          <div className="rlpn-export-field">
            <p className="rlpn-export-field-label">
              Start date <span className="rlpn-export-required">*</span>
            </p>
            <TextField
              id="rlpn-export-start-date"
              label=""
              type="date"
              value={startDate}
              required
              hasErrors={startDateError !== ''}
              errorMessage={startDateError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newStart = e.target.value
                setStartDate(newStart)
                setStartDateError('')
                setEndDateError('')
                if (!newStart.trim()) {
                  setEndDate('')
                }
              }}
            />
          </div>
          <TextField
            id="rlpn-export-end-date"
            label="End date"
            type="date"
            value={endDate}
            min={startDate}
            disabled={!startDate.trim()}
            hasErrors={!startDate.trim() || endDateError !== '' || endBeforeStart || rangeExceeded}
            errorMessage={
              !startDate.trim()
                ? 'Please enter Start date first'
                : endDateError
                  || (endBeforeStart ? 'End date must be on or after start date' : '')
                  || (rangeExceeded ? MAX_RLPN_EXPORT_RANGE_ERROR : '')
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEndDate(e.target.value)
              setEndDateError('')
            }}
          />
        </div>
      </div>
    </Modal>
  )
}
